import _ from "csi/_base/underscore";
import crews from "csi/access/crews";
import { CSS_TABLE_COLUMNS } from "csi/constants";
import { Context } from "csi/def/Context";
import Heading from "csi/def/Heading";
import Spec from "csi/heading/Spec";
import Nodes from "csi/store/Nodes";
import PathMatrix from "csi/table/matrix/Path";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import declare from "dojo/_base/declare";
import { C8Node } from "src/c8-platform/type/node";

const PRODUCT_COMPLIANCE_ANALYSIS_ADMINISTRATOR = "Product Compliance Analysis Administrator";

const ParameterValueRefmapMatrix = declare(PathMatrix, {
	_nodeColumnStore: null,
	_complianceParameterValueMap: new Map(),
	_userCanEdit: false,
	columnIndexAttribute: "",
	nodeType: "ProductComplianceAnalysisType",

	addDeferred(ctx: Context, h: Heading, dfds: PromiseLike<any>[]): void {
		this._userCanEdit = crews.isInRoles([PRODUCT_COMPLIANCE_ANALYSIS_ADMINISTRATOR]);

		// C8FB-2899: The FlatIngredientItem.ComplianceAnalysisFunctions matrix will show only ProductComplianceAnalysisType's for that BaseProduct Type
		const viewNode = ctx.view.getViewNode();
		const filter = new Query()
			.Attr("Active", xml.OP_EQUALS, true);

		if (viewNode?.$Type === "FoodLabelDataSheetRevision") {
			const parentProduct = ctx.cache.getNode(viewNode?.ParentProduct);
			const parentProductNodeType = parentProduct?.$Type;

			let pathToComplianceAnalysisTypes = null;
			if (parentProductNodeType === "Food" || parentProductNodeType === "FormulatedMaterial") {
				pathToComplianceAnalysisTypes = "Child:ParentProduct/Child:Subtype/Child:Settings/Child:ComplianceAnalysisTypes";
			} else if (parentProductNodeType === "Style" || parentProductNodeType === "Material") {
				pathToComplianceAnalysisTypes = "Child:ParentProduct/Child:ProductType/Child:ProductionConfig/Child:ComplianceAnalysisTypes";
			}

			this._nodeColumnStore = new Nodes({
				cachingStore: ctx.cache,
				filterQuery: filter,
				typeNode: this.nodeType,
				requestParameters: {
					"Qry.Limit.Path": xml.getUrlPath(ctx.view.urlRoot, pathToComplianceAnalysisTypes),
				},
			});
		} else {
			this._nodeColumnStore = new Nodes({
				typeNode: this.nodeType,
				filterQuery: filter,
			});
		}

		dfds.push(this._nodeColumnStore.query());
	},

	getColumnSpecs(ctx: Context, h: Heading): Spec[] {
		// 2nd column Specs
		const subs = [];
		this._nodeColumnStore.query().map((columnNode) => {
			const sc = new Spec({
				css: CSS_TABLE_COLUMNS,
				index: columnNode[this.columnIndexAttribute],
				heading: h,
				headerParams: {
					innerText: columnNode.$Name,
				},
			});

			subs.push(sc);
		});

		const spec = new Spec({
			css: CSS_TABLE_COLUMNS,
			heading: h,
			headerParams: {
				colSpan: subs.length,
				multiLineText: h.m_name,
			},
		});

		spec.subs = subs;
		return [spec];
	},

	_getRowNodes: function (ctx, qr, c, node) {
		return [node];
	},

	/**
	 * @override
	 * Grey out cells without a ProductComplianceAnalysisType configured for the BaseProduct Type in that column
	 */
	_getSpecNodes(ctx: Context, spec: Spec, nodes: C8Node[]): C8Node[] {
		if (!this._userCanEdit) {
			ctx.setReadOnly(true);
		}

		const currentNode = ctx.getNode();
		const currentNodeType = currentNode?.$Type;

		if (currentNodeType === "FoodIngredient" || currentNodeType === "FlatIngredientItem") {
			return this.inherited<C8Node[]>(arguments);
		}

		let currentNodeConfig = null;
		if (currentNodeType === "Food" || currentNodeType === "FormulatedMaterial") {
			currentNodeConfig = currentNode?.Subtype;
		} else if (currentNodeType === "Style" || currentNodeType === "Material") {
			currentNodeConfig = currentNode?.ProductType;
		}
		
		if (!this._complianceParameterValueMap.has(currentNodeConfig)) {
			this._complianceParameterValueMap.set(currentNodeConfig, new Map());
		}

		const currentNodeMap = this._complianceParameterValueMap.get(currentNodeConfig);
		if (!currentNodeMap.has(spec?.index)) {	
			const hasComplianceAnalysisTypes = this._hasComplianceAnalysisTypes(ctx, currentNode, spec);

			if (hasComplianceAnalysisTypes) {
				currentNodeMap.set(spec?.index, hasComplianceAnalysisTypes);
			} else if (!currentNodeMap.has(spec?.index) || !currentNodeMap.get(spec?.index)) {
				currentNodeMap.set(spec?.index, false);
			}
		}

		if (currentNodeMap.get(spec?.index)) {
			return this.inherited<C8Node[]>(arguments);
		}
		return [];
	},

	/**
	 * Returns whether or not the current BaseProduct node has ComplianceAnalysisTypes
	 */
	_hasComplianceAnalysisTypes(ctx: Context, currentNode: C8Node, spec: Spec) {
		const currentNodeType = currentNode?.$Type;

		let complianceAnalysisTypes = null;
		if (currentNodeType === "Food" || currentNodeType === "FormulatedMaterial") {
			const subtype = ctx.cache.getNode(currentNode?.Subtype);
			const settings = ctx.cache.getNode(subtype?.Settings);
			complianceAnalysisTypes = settings?.ComplianceAnalysisTypes;
		} else if (currentNodeType === "Style" || currentNodeType === "Material") {
			const productType = ctx.cache.getNode(currentNode?.ProductType);
			const productionConfig = ctx.cache.getNode(productType?.ProductionConfig);
			complianceAnalysisTypes = productionConfig?.ComplianceAnalysisTypes;
		}

		if (!_.isEmpty(complianceAnalysisTypes)) {
			for (const complianceAnalysisTypeCNL of complianceAnalysisTypes) {
				const complianceAnalysisType = ctx.cache.getNode(complianceAnalysisTypeCNL);
				const requestAnalysisType = complianceAnalysisType?.RequestAnalysisType;
				if (requestAnalysisType != null && requestAnalysisType === spec?.index) {
					return true;
				}
			}
		}
		return false;
	},

});

export = ParameterValueRefmapMatrix;